import { useEffect, useState } from 'react';
import { getOrCreateDeviceId } from '@/utils/deviceIdentity';
import { useBansEnabled } from '@/hooks/useBansEnabled';

type DeviceIdentity = {
  deviceId: string;
  visitorId: string;
  ready: boolean;
};

let cached: DeviceIdentity | null = null;
let inflight: Promise<DeviceIdentity> | null = null;

async function loadIdentity(): Promise<DeviceIdentity> {
  if (cached) {
    return cached;
  }
  if (inflight) {
    return inflight;
  }
  inflight = (async () => {
    const deviceId = await getOrCreateDeviceId();
    let visitorId = '';
    try {
      const FingerprintJS = (await import('@fingerprintjs/fingerprintjs'))
        .default;
      const agent = await FingerprintJS.load();
      const result = await agent.get();
      visitorId = result.visitorId;
    } catch {
      visitorId = '';
    }
    cached = { deviceId, visitorId, ready: true };
    return cached;
  })();
  return inflight;
}

export function useDeviceIdentity(): DeviceIdentity {
  const { bansEnabled } = useBansEnabled();
  const [state, setState] = useState<DeviceIdentity>(
    cached ?? { deviceId: '', visitorId: '', ready: !bansEnabled },
  );

  useEffect(() => {
    if (!bansEnabled) {
      setState((prev) => ({ ...prev, ready: true }));
      return;
    }
    let cancelled = false;
    void loadIdentity().then((identity) => {
      if (!cancelled) {
        setState(identity);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [bansEnabled]);

  return state;
}

export function useDeviceSignalVariables() {
  const identity = useDeviceIdentity();
  return {
    ...identity,
    variables: {
      deviceId: identity.deviceId || undefined,
      visitorId: identity.visitorId || undefined,
    },
  };
}
